import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target, FolderOpen, BarChart3, Users, ShieldCheck, Crown,
  Settings, ChevronLeft, ChevronRight, Menu, Building2,
  ChevronDown, UserCheck, UsersRound, Layers3, Plug,
  LayoutGrid, CalendarDays, Zap, Sparkles, UserCog, ArrowLeft, BookOpen, AlertCircle,
  ClipboardList, ClipboardCheck, Archive, Settings2, ScrollText, LifeBuoy,
  LogOut, X,
} from "lucide-react";
import { pageMotionVariants } from "../../lib/ui-motion";
import { useAuth } from "../../App";

const ALL_NAV_ITEMS = [
  { label: "Dashboard",   icon: Target,          path: "/dashboard"   },
  { label: "My Tasks",    icon: ClipboardList,   path: "/my-tasks"    },
  { label: "Approvals",   icon: ClipboardCheck,  path: "/approvals"   },
  { label: "Projects",    icon: FolderOpen,      path: "/projects"    },
  { label: "Analytics",   icon: BarChart3,       path: "/analytics"   },
  {
    label: "Directory",
    icon: Users,
    path: "/team",
    subItems: [
      { label: "Users",    icon: UserCheck,  path: "/team/users"    },
      { label: "Activity", icon: BarChart3,  path: "/team/activity" },
      { label: "Teams",    icon: UsersRound, path: "/team/teams"    },
      { label: "Groups",   icon: Layers3,    path: "/team/groups"   },
    ],
  },
  { label: "Integrations", icon: Plug,        path: "/integrations" },
  { label: "Audit Log",    icon: ScrollText,  path: "/audit-log", adminOnly: true },
  { label: "Enterprise",   icon: ShieldCheck, path: "/enterprise"   },
  { label: "Billing",      icon: Crown,       path: "/billing"      },
  { label: "Settings",     icon: Settings,    path: "/settings"     },
];

/* Floating popover shown when sidebar is collapsed and a group item is clicked */
const SubPopover = ({ item, anchorRef, onClose }) => {
  const popRef  = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (
        popRef.current    && !popRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  if (!item?.subItems) return null;

  const top = anchorRef.current
    ? anchorRef.current.getBoundingClientRect().top + window.scrollY
    : 0;

  return (
    <div
      ref={popRef}
      style={{ top }}
      className="fixed left-[68px] z-[200] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 min-w-[164px]"
    >
      <div className="px-3 py-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 mb-1">
        {item.label}
      </div>
      {item.subItems.map(sub => {
        const active = location.pathname === sub.path || location.pathname.startsWith(sub.path + "/");
        return (
          <Link
            key={sub.path}
            to={sub.path}
            onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors
              ${active
                ? "bg-[#f3eeff] dark:bg-[#4B1E87]/20 text-[#4B1E87] dark:text-[#875AC3] font-medium"
                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"}`}
          >
            <sub.icon className="w-3.5 h-3.5 shrink-0" />
            {sub.label}
          </Link>
        );
      })}
    </div>
  );
};

const AppShell = ({ header, children, className = "" }) => {
  const location = useLocation();
  const { currentOrg, user, logout } = useAuth();
  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [expandedNav, setExpandedNav] = useState(() =>
    location.pathname.startsWith("/team") ? "/team" : null
  );
  const [popover,       setPopover]       = useState(null);
  const popoverAnchorRef                  = useRef(null);

  const wsName      = currentOrg?.name || "";
  const orgRole     = (currentOrg?.role || "").toUpperCase();
  const isAdminOrOwner = ["OWNER", "ADMIN"].includes(orgRole);
  const navItems    = ALL_NAV_ITEMS.filter(item => !item.adminOnly || isAdminOrOwner);
  const toggleExpand = (path) => setExpandedNav(p => p === path ? null : path);

  // Close drawer on ESC key
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Prevent background scrolling while drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // ── Project sub-nav detection ──────────────────────────────────────────────
  const pathSegments = location.pathname.split('/');
  // Matches /projects/<uuid>/<section> — uuid is always >8 chars
  const onProjectRoute = pathSegments[1] === 'projects' && (pathSegments[2]?.length ?? 0) > 8;
  const activeProjectId = onProjectRoute ? pathSegments[2] : null;
  const activeSection   = onProjectRoute ? (pathSegments[3] || 'board') : null;

  const PROJECT_SUBNAV = [
    { label: 'Board',         icon: LayoutGrid,   section: 'board'    },
    { label: 'Timeline',      icon: CalendarDays, section: 'timeline' },
    { label: 'Sprints',       icon: Zap,          section: 'sprints'  },
    { label: 'Assign Tasks',  icon: UserCheck,    section: 'assign'   },
    { label: 'Issues',        icon: AlertCircle,  section: 'issues'   },
    { label: 'Archived',      icon: Archive,      section: 'archived' },
    { label: 'AI Tools',      icon: Sparkles,     section: 'ai'       },
    { label: 'Members',       icon: UserCog,      section: 'members'  },
    { label: 'Settings',      icon: Settings2,    section: 'settings' },
  ];

  /* ── Sidebar contents (shared between desktop & mobile) ── */
  const SidebarContent = ({ isMobile = false }) => {
    const isCollapsed = collapsed && !isMobile;

    return (
      <div
        className="flex flex-col h-full select-none"
        style={isMobile ? {
          paddingTop:    'env(safe-area-inset-top,    0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        } : {}}
      >

        {/* ── Mobile: header row with logo + close button ── */}
        {isMobile ? (
          <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-gray-100 dark:border-gray-800 shrink-0">
            <img src="/syncwork.svg" alt="SyncWork" className="h-10 w-auto object-contain" style={{maxWidth:'140px'}} />
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation menu"
              className="flex items-center justify-center w-11 h-11 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        ) : (
          /* Desktop: Brand logo */
          <div className={`flex items-center px-3 pt-3 pb-3 ${isCollapsed ? "justify-center" : ""}`}>
            {isCollapsed ? (
              <img src="/syncwork-app-icon.svg" alt="SyncWork" className="h-10 w-10 object-contain shrink-0" />
            ) : (
              <img src="/syncwork.svg" alt="SyncWork" className="h-20 w-auto object-contain" style={{maxWidth:'200px'}} />
            )}
          </div>
        )}

        {/* ── Mobile: authenticated user info ── */}
        {isMobile && user && (
          <div className="mx-3 mt-3 p-3 bg-[#f3eeff] dark:bg-[#4B1E87]/20 rounded-xl border border-[#ddd0f5] dark:border-[#4B1E87]/30 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4B1E87] to-[#693CB4] flex items-center justify-center text-white text-sm font-bold shrink-0">
                {(user.full_name || user.email || "U")[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
                  {user.full_name || "User"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                {currentOrg?.role && (
                  <p className="text-[11px] text-[#4B1E87] dark:text-indigo-400 font-medium mt-0.5 capitalize">
                    {currentOrg.role}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Workspace chip (desktop only — mobile shows org role in user info block) */}
        {wsName && !isCollapsed && !isMobile && (
          <div className="mx-3 mt-3 mb-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
            <p className="text-[13px] font-medium text-gray-700 dark:text-gray-200 truncate leading-snug">{wsName}</p>
          </div>
        )}
        {wsName && isCollapsed && (
          <div className="flex justify-center mt-3 mb-2">
            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center" title={wsName}>
              <Building2 className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
        )}
        {/* Mobile workspace chip (below user info) */}
        {wsName && isMobile && (
          <div className="mx-3 mt-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center gap-2 shrink-0">
            <Building2 className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
            <p className="text-[13px] font-medium text-gray-700 dark:text-gray-200 truncate leading-snug">{wsName}</p>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const isParentActive =
              location.pathname === item.path ||
              (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
            const isExpanded   = expandedNav === item.path;
            const hasChildren  = !!item.subItems?.length;

            // ── Projects item: when inside a project, expand inline to show project tools ──
            if (item.path === "/projects" && onProjectRoute) {
              return (
                <div key={item.path}>
                  {/* "← Projects" back link */}
                  <Link
                    to="/projects"
                    onClick={() => isMobile && setMobileOpen(false)}
                    title={isCollapsed ? "Projects" : undefined}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-100 group
                      ${isCollapsed ? "justify-center" : ""}
                      text-[#4B1E87] dark:text-[#875AC3] bg-indigo-50 dark:bg-indigo-900/30`}
                  >
                    <FolderOpen className="w-4 h-4 shrink-0 text-[#4B1E87] dark:text-[#875AC3]" />
                    {!isCollapsed && (
                      <>
                        <ArrowLeft className="w-3 h-3 mr-0.5 opacity-60" />
                        <span className="truncate flex-1">Projects</span>
                      </>
                    )}
                  </Link>

                  {/* Project tools nested under Projects */}
                  {!isCollapsed && (
                    <div className="mt-0.5 ml-4 pl-3 border-l-2 border-[#e0d0f7] dark:border-[#4B1E87]/30 space-y-0.5 pb-1">
                      {PROJECT_SUBNAV.map(sub => {
                        const active = activeSection === sub.section;
                        return (
                          <Link
                            key={sub.section}
                            to={`/projects/${activeProjectId}/${sub.section}`}
                            onClick={() => isMobile && setMobileOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-100 group
                              ${active
                                ? "bg-[#f3eeff] dark:bg-[#4B1E87]/20 text-[#4B1E87] dark:text-[#875AC3] font-medium"
                                : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-white"}`}
                          >
                            <sub.icon className={`w-3.5 h-3.5 shrink-0 ${active ? "text-[#5F2AA8] dark:text-[#875AC3]" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"}`} />
                            <span className="truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  {/* Collapsed state: show project tools as icons */}
                  {isCollapsed && (
                    <div className="mt-0.5 space-y-0.5">
                      {PROJECT_SUBNAV.map(sub => {
                        const active = activeSection === sub.section;
                        return (
                          <Link
                            key={sub.section}
                            to={`/projects/${activeProjectId}/${sub.section}`}
                            title={sub.label}
                            className={`flex items-center justify-center px-3 py-2 rounded-lg transition-colors duration-100
                              ${active
                                ? "bg-[#f3eeff] dark:bg-[#4B1E87]/20 text-[#4B1E87] dark:text-[#875AC3]"
                                : "text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600"}`}
                          >
                            <sub.icon className="w-4 h-4" />
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            if (hasChildren) {
              const anchorRef = { current: null };
              return (
                <div key={item.path}>
                  {/* Parent row */}
                  <button
                    ref={el => { if (el) anchorRef.current = el; }}
                    onClick={() => {
                      if (isCollapsed) {
                        setPopover(prev => prev?.path === item.path ? null : item);
                        popoverAnchorRef.current = anchorRef.current;
                        // On mobile (shouldn't be collapsed, but guard anyway)
                        if (isMobile) setMobileOpen(false);
                      } else {
                        // Expand/collapse the submenu — keep mobile drawer open
                        toggleExpand(item.path);
                      }
                    }}
                    title={isCollapsed ? item.label : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-100 group
                      ${isCollapsed ? "justify-center" : ""}
                      ${isParentActive
                        ? "bg-[#f3eeff] dark:bg-[#4B1E87]/20 text-[#4B1E87] dark:text-[#875AC3]"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"}`}
                  >
                    <item.icon className={`w-4 h-4 shrink-0 ${isParentActive ? "text-[#4B1E87] dark:text-[#875AC3]" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"}`} />
                    {!isCollapsed && (
                      <>
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 text-gray-400 dark:text-gray-500 ${isExpanded ? "rotate-180" : ""}`} />
                      </>
                    )}
                  </button>

                  {/* Sub-items */}
                  {!isCollapsed && isExpanded && (
                    <div className="mt-0.5 ml-4 pl-3 border-l-2 border-gray-100 dark:border-gray-800 space-y-0.5 pb-1">
                      {item.subItems.map(sub => {
                        const subActive = location.pathname === sub.path || location.pathname.startsWith(sub.path + "/");
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            onClick={() => isMobile && setMobileOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-100 group
                              ${subActive
                                ? "bg-[#f3eeff] dark:bg-[#4B1E87]/20 text-[#4B1E87] dark:text-[#875AC3] font-medium"
                                : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-white"}`}
                          >
                            <sub.icon className={`w-3.5 h-3.5 shrink-0 ${subActive ? "text-[#5F2AA8] dark:text-[#875AC3]" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"}`} />
                            <span className="truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            /* Leaf nav item */
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => isMobile && setMobileOpen(false)}
                title={isCollapsed ? item.label : undefined}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-100 group
                  ${isCollapsed ? "justify-center" : ""}
                  ${isParentActive
                    ? "bg-[#f3eeff] dark:bg-[#4B1E87]/20 text-[#4B1E87] dark:text-[#875AC3]"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"}`}
              >
                <item.icon className={`w-4 h-4 shrink-0 ${isParentActive ? "text-[#4B1E87] dark:text-[#875AC3]" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"}`} />
                {!isCollapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Guide + Help links + (mobile) Logout */}
        <div className="px-3 pb-3 mt-auto pt-2 border-t border-gray-100 dark:border-gray-800 space-y-0.5 shrink-0">
          <a
            href="/guide"
            target="_blank"
            rel="noopener noreferrer"
            title={isCollapsed ? "User Guide" : undefined}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-100 group text-gray-500 dark:text-gray-400 hover:bg-[#f3eeff] dark:hover:bg-[#4B1E87]/20 hover:text-[#4B1E87] dark:hover:text-[#875AC3] ${isCollapsed ? "justify-center" : ""}`}
          >
            <BookOpen className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-indigo-500" />
            {!isCollapsed && <span className="truncate">User Guide</span>}
          </a>
          <Link
            to="/help"
            onClick={() => isMobile && setMobileOpen(false)}
            title={isCollapsed ? "Help & Support" : undefined}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-100 group text-gray-500 dark:text-gray-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-600 dark:hover:text-rose-400 ${isCollapsed ? "justify-center" : ""}`}
          >
            <LifeBuoy className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-rose-500" />
            {!isCollapsed && <span className="truncate">Help & Support</span>}
          </Link>

          {/* Logout — mobile drawer only */}
          {isMobile && user && (
            <button
              onClick={() => { setMobileOpen(false); logout(); }}
              className="flex items-center w-full gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-100 mt-1"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              <span className="truncate">Sign Out</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-950 flex ${className}`}>

      {/* ── Desktop Sidebar ── */}
      <aside className={`hidden md:flex flex-col ${collapsed ? "w-[60px]" : "w-[220px]"} bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 sticky top-0 h-screen transition-[width] duration-200 z-30 shrink-0 overflow-visible`}>
        <SidebarContent />

        {/* Collapse toggle */}
        <button
          onClick={() => { setCollapsed(c => !c); setPopover(null); }}
          className="absolute -right-3 top-[52px] w-6 h-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors z-40"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed
            ? <ChevronRight className="w-3 h-3 text-gray-400" />
            : <ChevronLeft  className="w-3 h-3 text-gray-400" />}
        </button>
      </aside>

      {/* Floating popover for collapsed state */}
      {collapsed && popover && (
        <SubPopover
          item={popover}
          anchorRef={popoverAnchorRef}
          onClose={() => setPopover(null)}
        />
      )}

      {/* ── Mobile sidebar overlay ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <aside className="w-[min(280px,80vw)] bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-xl overflow-y-auto">
            <SidebarContent isMobile />
          </aside>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile top bar — fixed so it stays visible while content scrolls */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center gap-3 px-3 h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            className="flex items-center justify-center w-11 h-11 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
          >
            <Menu className="w-6 h-6 text-[#4B1E87] dark:text-indigo-400" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <img src="/syncwork.svg" alt="SyncWork" className="h-10 w-auto object-contain" style={{maxWidth:'150px'}} />
          </div>
        </div>

        {/* Desktop header */}
        <div className="hidden md:block sticky top-0 z-20">
          {header}
        </div>

        {/* Page content — pt-14 on mobile offsets the fixed 56px top bar */}
        <motion.main
          className="flex-1 overflow-auto pt-14 md:pt-0"
          initial={pageMotionVariants.initial}
          animate={pageMotionVariants.animate}
          exit={pageMotionVariants.exit}
          transition={pageMotionVariants.transition}
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
};

export default AppShell;
